import { useMemo } from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import {
  CHART_PRIMARY_GOLD,
  CHART_TOOLTIP_PROPS,
  chartAxisProps,
  chartGridProps,
} from '@/config/chartTheme'
import {
  OUTCOMES,
  OUTCOME_COLOR,
  OUTCOME_LABEL,
  NOT_RECORDED_COLOR,
  formatMeasure,
  goldCategory,
  groupLabel,
  sortRows,
  type ChartMeasure,
  type Outcome,
  type PivotRow,
} from '@/services/dashboardAnalytics'

const NULL_KEY = '__null__'
const OTHER_KEY = '__other__'
/** Segments drawn per bar before the rest fold into "Other". */
const MAX_SEGMENTS = 8

export interface PivotSegment {
  dataKey: string
  /** The child keys this segment stands for (several for "Other"). */
  keys: Array<string | null>
  label: string
  color: string
}

export interface PivotChartRow {
  rowKey: string | null
  label: string
  total: number
  [segment: string]: number | string | null
}

function value(measures: PivotRow['measures'] | undefined, measure: ChartMeasure): number {
  const raw = measures ? (measures as unknown as Record<string, unknown>)[measure] : null
  return typeof raw === 'number' && Number.isFinite(raw) ? raw : 0
}

/** Build the stacked series — pure, tested. Outcome children keep pipeline
 *  order and their own colours; any other "by" keeps its eight largest
 *  members and folds the rest into one "Other" segment. */
export function buildPivotSeries(
  rows: readonly PivotRow[],
  by: string | null,
  measure: ChartMeasure,
): { segments: PivotSegment[]; data: PivotChartRow[] } {
  if (!by) {
    return {
      segments: [{ dataKey: 'total', keys: [], label: 'Total', color: CHART_PRIMARY_GOLD }],
      data: rows.map((row) => ({
        rowKey: row.key,
        label: groupLabel(row.key, row.label),
        total: value(row.measures, measure),
      })),
    }
  }

  const totals = new Map<string, { key: string | null; label: string; total: number }>()
  for (const row of rows) {
    for (const child of row.children ?? []) {
      const dataKey = child.key == null ? NULL_KEY : child.key
      const entry = totals.get(dataKey) ?? { key: child.key, label: groupLabel(child.key, child.label), total: 0 }
      entry.total += value(child.measures, measure)
      totals.set(dataKey, entry)
    }
  }

  let ordered: Array<[string, { key: string | null; label: string; total: number }]>
  if (by === 'outcome') {
    ordered = OUTCOMES.filter((outcome) => totals.has(outcome)).map((outcome) => [
      outcome,
      totals.get(outcome)!,
    ])
    if (totals.has(NULL_KEY)) ordered.push([NULL_KEY, totals.get(NULL_KEY)!])
  } else {
    ordered = [...totals.entries()].sort((a, b) => b[1].total - a[1].total)
  }

  const kept = ordered.slice(0, MAX_SEGMENTS)
  const folded = ordered.slice(MAX_SEGMENTS)
  const segments: PivotSegment[] = kept.map(([dataKey, entry], index) => ({
    dataKey,
    keys: [entry.key],
    label: by === 'outcome' && entry.key && entry.key in OUTCOME_LABEL ? OUTCOME_LABEL[entry.key as Outcome] : entry.label,
    color:
      by === 'outcome' && entry.key && entry.key in OUTCOME_COLOR
        ? OUTCOME_COLOR[entry.key as Outcome]
        : entry.key == null
          ? NOT_RECORDED_COLOR
          : goldCategory(index),
  }))
  if (folded.length) {
    segments.push({
      dataKey: OTHER_KEY,
      keys: folded.map(([, entry]) => entry.key),
      label: `Other (${folded.length})`,
      color: '#55524B',
    })
  }
  const foldedKeys = new Set(folded.map(([dataKey]) => dataKey))

  const data = rows.map((row) => {
    const out: PivotChartRow = {
      rowKey: row.key,
      label: groupLabel(row.key, row.label),
      total: value(row.measures, measure),
    }
    for (const segment of segments) out[segment.dataKey] = 0
    for (const child of row.children ?? []) {
      const dataKey = child.key == null ? NULL_KEY : child.key
      const target = foldedKeys.has(dataKey) ? OTHER_KEY : dataKey
      out[target] = (Number(out[target]) || 0) + value(child.measures, measure)
    }
    return out
  })
  return { segments, data }
}

/** A category name on one line, right-aligned to the axis. Recharts wraps a
 *  long name onto the next bar's row; the full name is in the tooltip. */
function CategoryTick(props: { x?: number; y?: number; payload?: { value?: string } }) {
  const { x = 0, y = 0, payload } = props
  const label = String(payload?.value ?? '')
  const shown = label.length > 26 ? `${label.slice(0, 25)}…` : label
  return (
    <text x={x} y={y} dy={3} textAnchor="end" fill="#a2a2a2" fontSize={9.5}>
      <title>{label}</title>
      {shown}
    </text>
  )
}

interface PivotChartProps {
  rows: readonly PivotRow[]
  by: string | null
  measure: ChartMeasure
  /** Months, bands and stages read left to right in their own order. */
  ordered: boolean
  /** Bars drawn for an unordered dimension; the table lists every row. */
  maxBars?: number
  onSelectRow?: (key: string | null) => void
  onSelectSegment?: (rowKey: string | null, childKeys: Array<string | null>) => void
}

/**
 * The Explorer's chart. A ranked dimension draws horizontal bars, largest
 * first; an ordered one (months, bands, stages) draws columns in its own
 * order. Clicking a bar narrows the cohort to it — the table below is where
 * each number opens its deals.
 */
export default function PivotChart({
  rows,
  by,
  measure,
  ordered,
  maxBars = 16,
  onSelectRow,
  onSelectSegment,
}: PivotChartProps) {
  const shown = useMemo(
    () => (ordered ? [...rows] : sortRows(rows, measure, 'desc').slice(0, maxBars)),
    [rows, ordered, measure, maxBars],
  )
  const { segments, data } = useMemo(() => buildPivotSeries(shown, by, measure), [shown, by, measure])
  const format = (amount: number) => formatMeasure(amount, measure)

  if (data.length === 0) {
    return <p className="px-1 py-6 text-center text-[11px] text-stone-500">No deal in this cohort.</p>
  }

  const click = (segment: PivotSegment) => (entry: { payload?: PivotChartRow }) => {
    const row = entry?.payload
    if (!row) return
    if (onSelectSegment && segment.keys.length > 0) onSelectSegment(row.rowKey, segment.keys)
    else onSelectRow?.(row.rowKey)
  }

  const labelWidth = Math.min(
    160,
    Math.max(70, ...data.map((row) => Math.min(26, String(row.label).length) * 5.6 + 8)),
  )

  return (
    <ResponsiveContainer width="100%" height="100%">
      {ordered ? (
        <BarChart data={data} margin={{ top: 6, right: 8, left: -4, bottom: 0 }} barCategoryGap="18%">
          <CartesianGrid {...chartGridProps()} />
          <XAxis dataKey="label" {...chartAxisProps(9)} interval="preserveStartEnd" minTickGap={8} />
          <YAxis {...chartAxisProps(9)} tickFormatter={format} width={measure === 'count' ? 32 : 54} />
          <Tooltip {...CHART_TOOLTIP_PROPS} cursor={{ fill: 'rgba(217,183,104,0.05)' }} formatter={(v: number, name: string) => [format(v), name]} />
          {segments.length > 1 && <Legend iconSize={8} wrapperStyle={{ fontSize: 9.5, color: '#a2a2a2' }} />}
          {segments.map((segment, index) => (
            <Bar
              key={segment.dataKey}
              dataKey={segment.dataKey}
              name={segment.label}
              stackId="stack"
              fill={segment.color}
              cursor="pointer"
              maxBarSize={28}
              radius={index === segments.length - 1 ? [2, 2, 0, 0] : undefined}
              onClick={click(segment)}
            />
          ))}
        </BarChart>
      ) : (
        <BarChart data={data} layout="vertical" margin={{ top: 2, right: 12, left: 0, bottom: 0 }} barCategoryGap="22%">
          <CartesianGrid {...chartGridProps(false)} />
          <XAxis type="number" {...chartAxisProps(9)} tickFormatter={format} />
          <YAxis
            type="category"
            dataKey="label"
            {...chartAxisProps(9.5)}
            width={labelWidth}
            interval={0}
            tick={<CategoryTick />}
          />
          <Tooltip {...CHART_TOOLTIP_PROPS} cursor={{ fill: 'rgba(217,183,104,0.05)' }} formatter={(v: number, name: string) => [format(v), name]} />
          {segments.length > 1 && <Legend iconSize={8} wrapperStyle={{ fontSize: 9.5, color: '#a2a2a2' }} />}
          {segments.map((segment, index) => (
            <Bar
              key={segment.dataKey}
              dataKey={segment.dataKey}
              name={segment.label}
              stackId="stack"
              fill={segment.color}
              cursor="pointer"
              maxBarSize={16}
              radius={index === segments.length - 1 ? [0, 2, 2, 0] : undefined}
              onClick={click(segment)}
            />
          ))}
        </BarChart>
      )}
    </ResponsiveContainer>
  )
}
